import { createContext, useContext, type ReactNode } from 'react'

const ScopedFieldContext = createContext<string | null>(null)

interface ScopedFieldProviderProps {
  fieldId: string | null
  children: ReactNode
}

/**
 * Restricts an editor subtree to a single focused section. Consumed by
 * `FieldGroup` to hide every block whose `sectionId` doesn't match `fieldId`.
 *
 * `fieldId === null` ⇒ no scoping (render the whole editor).
 */
export function ScopedFieldProvider({ fieldId, children }: ScopedFieldProviderProps) {
  return <ScopedFieldContext.Provider value={fieldId}>{children}</ScopedFieldContext.Provider>
}

export function useScopedFieldId(): string | null {
  return useContext(ScopedFieldContext)
}

/**
 * Returns true if the subtree is scoped AND the given sectionId does NOT match.
 * FieldGroup-style helpers should bail out (return null) when this is true.
 */
export function useIsScopedOut(sectionId: string | undefined): boolean {
  const scopedTo = useContext(ScopedFieldContext)
  if (scopedTo === null) return false
  if (!sectionId) return true
  return scopedTo !== sectionId
}
